import React from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, Typography, Box, Button } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import { useDrop } from 'react-dnd'
import OrderCardFullScreenPlanning from './OrderCardFullScreenPlanning'
import { useTranslation } from 'react-i18next'

const ItemTypes = {
  ORDER: 'order',
}

const DriverCardFullScreen = ({ driver, transporterColor, moveOrder, toggleDriverStatus, isDragDisabled }) => {
  const { t } = useTranslation('planning_page')
  const [{ canDrop }, drop] = useDrop({
    accept: ItemTypes.ORDER,
    drop: (item) => {
      if (!driver.isOutOfOfficeToday) {
        moveOrder(item, driver.id, item.fromDriverId || null)
      }
    },
    canDrop: () => !driver.isOutOfOfficeToday,
    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
    }),
  })

  return (
    <Card
      ref={drop}
      sx={{
        display: 'flex',
        padding: '16px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        borderRadius: '4px',
        border: '1px solid #C4C4C4',
        background: !driver.isOutOfOfficeToday ? '#F4F4F4' : '#E0E0E0',
        opacity: !driver.isOutOfOfficeToday ? 1 : 0.6,
        ...(canDrop ? { outline: '2px dashed #b0b0b0', outlineOffset: -2 } : {}),
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, width: '100%' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" mb={1}>
          <Box display="flex" alignItems="center" gap={1} flex={1} width="100%">
            <PersonIcon sx={{ color: !driver.isOutOfOfficeToday ? transporterColor : 'gray', stroke: !driver.isOutOfOfficeToday ? transporterColor : 'gray', position: 'static', width: 32, height: 32 }} />
            <Box display="flex" flexDirection="column">
              <Typography variant="body1" sx={{ fontSize: 14 }}>
                {driver.firstname} {driver.lastname}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 10 }}>
                  {driver.transporter.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 10 }}>-</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => toggleDriverStatus(driver.id)}
                  sx={{ fontSize: 10, border: 'none', minWidth: 0, p: 0, textTransform: 'none', height: '10px', color: '#252525', '&:hover': { border: 'none', color: '#1577FC' } }}
                  disabled={isDragDisabled}
                >
                  {!driver.isOutOfOfficeToday ? t('activeDriver') : t('inactiveDriver')}
                </Button>
              </Box>
            </Box>
          </Box>
          <Box>
            <Box
              sx={{
                display: 'flex',
                padding: '4px 8px',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                gap: '10px',
                borderRadius: '100px',
                border: driver.orderDeliveries.edges.length > 0 ? 'none' : '1px solid #3D3D3D',
                background: driver.orderDeliveries.edges.length > 0 ? '#4A809A' : 'transparent',
                boxShadow: driver.orderDeliveries.edges.length > 0 ? '0 1px 4px 0 rgba(0, 0, 0, 0.25)' : 'none',
                color: driver.orderDeliveries.edges.length > 0 ? '#FFF' : undefined,
                whiteSpace: 'nowrap',
                minWidth: 44,
                minHeight: 20,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {driver.orderDeliveries.edges.length} ODL
            </Box>
          </Box>
        </Box>
        {!driver.isOutOfOfficeToday && (
          driver.orderDeliveries.edges.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...driver.orderDeliveries.edges]
                .sort((a, b) => a.node.deliveryTimeStart.localeCompare(b.node.deliveryTimeStart))
                .map((order) => (
                <OrderCardFullScreenPlanning
                  key={order.node.id}
                  deliveryOrder={order.node}
                  fromDriverId={driver.id}
                  isDragDisabled={isDragDisabled}
                />
              ))}
            </Box>
          )
        )}
      </CardContent>
    </Card>
  )
}

DriverCardFullScreen.propTypes = {
  driver: PropTypes.any.isRequired,
  transporterColor: PropTypes.string.isRequired,
  moveOrder: PropTypes.func.isRequired,
  toggleDriverStatus: PropTypes.func.isRequired,
  isDragDisabled: PropTypes.bool,
}

export default DriverCardFullScreen
